import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Clock, Calendar, RefreshCw, TrendingUp, 
  DollarSign, Award, Building2, ClipboardList 
} from 'lucide-react';
import './StaffManagementModule.css';

// Import individual tab components
import StaffListTab from './tabs/StaffListTab';
import AttendanceTab from './tabs/AttendanceTab';
import LeaveManagementTab from './tabs/LeaveManagementTab';
import ShiftSchedulerTab from './tabs/ShiftSchedulerTab';
import DepartmentTab from './tabs/DepartmentTab';
import PayrollTab from './tabs/PayrollTab';
import PerformanceTab from './tabs/PerformanceTab';
import TaskHistoryTab from './tabs/TaskHistoryTab';

const StaffManagementModule = () => {
  const { token, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('staff');
  const [hotelId, setHotelId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Extract hotel_id from userProfile data - try multiple possible locations
    if (userProfile) {
      const extractedHotelId = userProfile.hotel_id || 
                               userProfile.hotelId || 
                               userProfile.managed_hotel_id ||
                               userProfile.managedHotels?.[0]?.hotel_id || 
                               null;
      
      console.log('[StaffManagementModule] userProfile:', userProfile);
      console.log('[StaffManagementModule] Extracted hotel_id:', extractedHotelId);
      console.log('[StaffManagementModule] token exists:', !!token);
      
      setHotelId(extractedHotelId);
      setLoading(false);
    }
  }, [userProfile, token]);

  const tabs = [
    {
      id: 'staff',
      label: 'Staff Directory',
      icon: Users,
      component: StaffListTab,
      description: 'Manage staff profiles and assignments'
    },
    {
      id: 'attendance',
      label: 'Attendance',
      icon: Clock,
      component: AttendanceTab,
      description: 'Track clock in/out and working hours'
    },
    {
      id: 'leave',
      label: 'Leave Management',
      icon: Calendar,
      component: LeaveManagementTab,
      description: 'Manage leave applications and approvals'
    },
    {
      id: 'shifts',
      label: 'Shift Scheduler',
      icon: RefreshCw,
      component: ShiftSchedulerTab,
      description: '24/7 shift planning and assignments'
    },
    {
      id: 'departments',
      label: 'Departments',
      icon: Building2,
      component: DepartmentTab,
      description: 'Department management and budgets'
    },
    {
      id: 'payroll',
      label: 'Payroll',
      icon: DollarSign,
      component: PayrollTab,
      description: 'Salary processing and payslips'
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: Award,
      component: PerformanceTab,
      description: 'Performance reviews and ratings'
    },
    {
      id: 'task-history',
      label: 'Task History',
      icon: ClipboardList,
      component: TaskHistoryTab,
      description: 'View assigned staff task logs and status changes'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // Show loading if still determining hotelId
  if (loading) {
    return (
      <div className="staff-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading hotel information...</p>
      </div>
    );
  }

  // Show error if no hotelId found
  if (!hotelId) {
    return (
      <div className="staff-management-module" style={{padding: '40px'}}>
        <div style={{textAlign: 'center', maxWidth: '600px', margin: '0 auto'}}>
          <h3 style={{color: '#ef4444', marginBottom: '16px'}}>Hotel ID Not Found</h3>
          <p style={{marginBottom: '20px'}}>Unable to determine your hotel. Please ensure you are assigned to a hotel.</p>
          <details style={{textAlign: 'left', background: '#f9fafb', padding: '16px', borderRadius: '8px', marginTop: '20px'}}>
            <summary style={{cursor: 'pointer', fontWeight: 600, marginBottom: '12px'}}>Debug: User Data</summary>
            <pre style={{fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap'}}>
              {JSON.stringify(userProfile, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-management-module">
      {/* Header with Module Title */}
      <div className="module-header">
        <div className="module-title">
          <Users className="module-icon" size={32} />
          <div>
            <h1>Staff Management System</h1>
            <p className="module-subtitle">Comprehensive employee management and operations</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tabs-container">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                title={tab.description}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Tab Content */}
      <div className="tab-content">
        {ActiveComponent && (
          <ActiveComponent 
            hotelId={hotelId} 
            token={token}
            user={userProfile}
          />
        )}
      </div>
    </div>
  );
};

export default StaffManagementModule;
